import { useEffect } from 'react';

import localStorageConstants from '~/lib/constants/localStorage.constants';
import useLocalStorage from '~/lib/hooks/useLocalStorage';

import { PATIENT_VIEWS, PatientViewKey } from '../constants';
import { isValidPatientView } from '../provider-dashboard.util';

/**
 * Remembers the provider dashboard's selected filter tab in localStorage, scoped per user, so the
 * selection survives page reloads. Returns a `useState`-like tuple. Falls back to the Active filter
 * for a first visit, cleared storage, or a stale/invalid stored value (and normalizes storage back
 * to the default in that case).
 */
const usePersistedPatientFilter = (
  userId: string,
): [PatientViewKey, (view: PatientViewKey) => void] => {
  const key = `${localStorageConstants.providerDashboardFilter}_${userId}`;
  const { storedValue, setValue } = useLocalStorage<PatientViewKey>(
    key,
    PATIENT_VIEWS.active,
  );

  const activeTab: PatientViewKey = isValidPatientView(storedValue)
    ? storedValue
    : PATIENT_VIEWS.active;

  useEffect(() => {
    if (!isValidPatientView(storedValue)) {
      setValue(PATIENT_VIEWS.active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedValue]);

  return [activeTab, setValue];
};

export default usePersistedPatientFilter;
